import { useSyncExternalStore } from "react";
import { useEditorTabPanelScrollRef } from "@/features/sparxsEditor/contexts/EditorTabPanelScrollContext";

const isHeaderRowVisibleInTabPanel = (
  headerRow: HTMLElement,
  scrollRoot: HTMLElement,
): boolean => {
  const headerRect = headerRow.getBoundingClientRect();
  const rootRect = scrollRoot.getBoundingClientRect();
  return (
    headerRect.height > 0 &&
    headerRect.bottom > rootRect.top &&
    headerRect.top < rootRect.bottom
  );
};

export const useEditorFieldHeaderVisibility = (
  headerRowElement: HTMLElement | null,
): boolean => {
  const tabPanelRef = useEditorTabPanelScrollRef();

  return useSyncExternalStore(
    (onStoreChange) => {
      const scrollRoot = tabPanelRef?.current ?? null;

      if (headerRowElement == null || scrollRoot == null) {
        return () => undefined;
      }

      const syncVisibility = () => {
        onStoreChange();
      };

      scrollRoot.addEventListener("scroll", syncVisibility, {
        passive: true,
      });
      window.addEventListener("resize", syncVisibility, { passive: true });

      const resizeObserver = new ResizeObserver(syncVisibility);
      resizeObserver.observe(scrollRoot);
      resizeObserver.observe(headerRowElement);

      return () => {
        scrollRoot.removeEventListener("scroll", syncVisibility);
        window.removeEventListener("resize", syncVisibility);
        resizeObserver.disconnect();
      };
    },
    () => {
      if (headerRowElement == null) {
        return true;
      }
      const scrollRoot = tabPanelRef?.current ?? null;
      if (scrollRoot == null) {
        return true;
      }
      return isHeaderRowVisibleInTabPanel(headerRowElement, scrollRoot);
    },
    () => true,
  );
};
